// app/components/privacy-graph-scrolly/elements/tier-lanes.tsx
//
// Vertical dashed dividers between tiers + tier headers snapped to top center
// of each lane. The AI/ML lane (tier 2) is hidden until beat 5 — its divider
// + header fade in during the materialization animation.

import { LAYOUT, TIER_LABELS, DIVIDER } from "../graph-layout";

const HEADER_PAD_TOP = 14;

interface TierBounds {
  tier: number;
  label: string | undefined;
  /** Center x of the tier (used for label anchor). */
  centerX: number;
  /** Right-edge x of the tier (where the divider sits). */
  dividerX: number;
}

function computeTierBounds(): TierBounds[] {
  const byTier = new Map<number, { left: number; right: number }>();
  for (const n of LAYOUT.nodes) {
    const curr = byTier.get(n.tier);
    byTier.set(n.tier, {
      left:  curr ? Math.min(curr.left, n.x) : n.x,
      right: curr ? Math.max(curr.right, n.x + n.w) : n.x + n.w,
    });
  }
  const tierIndices = [...byTier.keys()].sort((a, b) => a - b);
  return tierIndices.map((t) => {
    const b = byTier.get(t)!;
    const next = byTier.get(t + 1);
    const dividerX = next ? (b.right + next.left) / 2 : b.right + 24;
    return {
      tier: t,
      label: TIER_LABELS[t],
      centerX: (b.left + b.right) / 2,
      dividerX,
    };
  });
}

const TIER_BOUNDS = computeTierBounds();
export const AI_ML_TIER = 2;

interface Props {
  /** When false, the AI/ML divider + header are hidden. Driven by beat. */
  showAiml: boolean;
  /** Animated opacity for AI/ML divider + header (use with framer-motion outside if needed). */
  aimlOpacity?: number;
}

export function TierLanes({ showAiml, aimlOpacity = 1 }: Props) {
  const top = HEADER_PAD_TOP + 4;
  const bottom = LAYOUT.viewBox.h - 16;
  return (
    <g aria-hidden="true">
      {/* Dividers — between every adjacent pair of visible tiers. */}
      {TIER_BOUNDS.slice(0, -1).map((b) => {
        // Skip the divider between tiers 1 (COMPUTE) and 2 (AI/ML), and between 2 and 3,
        // when AI/ML is hidden — the COMPUTE/DATA boundary collapses to a single divider.
        const next = b.tier + 1;
        if (!showAiml && (b.tier === AI_ML_TIER - 1 || b.tier === AI_ML_TIER)) {
          // Show one divider between COMPUTE (1) and DATA (3) when AI/ML is hidden;
          // place at the COMPUTE/AI-ML midpoint as a fallback.
          if (b.tier === AI_ML_TIER - 1) {
            return (
              <line
                key={`div-fallback-${b.tier}`}
                x1={b.dividerX}
                y1={top}
                x2={b.dividerX}
                y2={bottom}
                stroke={DIVIDER.stroke}
                strokeWidth={DIVIDER.strokeWidth}
                strokeDasharray={DIVIDER.dash}
              />
            );
          }
          return null;
        }
        const opacity = next === AI_ML_TIER || b.tier === AI_ML_TIER ? aimlOpacity : 1;
        return (
          <line
            key={`div-${b.tier}`}
            x1={b.dividerX}
            y1={top}
            x2={b.dividerX}
            y2={bottom}
            stroke={DIVIDER.stroke}
            strokeWidth={DIVIDER.strokeWidth}
            strokeDasharray={DIVIDER.dash}
            opacity={opacity}
          />
        );
      })}

      {/* Tier headers */}
      {TIER_BOUNDS.map((b) => {
        if (b.tier === AI_ML_TIER && !showAiml) return null;
        const opacity = b.tier === AI_ML_TIER ? aimlOpacity : 1;
        return (
          <text
            key={`label-${b.tier}`}
            x={b.centerX}
            y={HEADER_PAD_TOP}
            textAnchor="middle"
            fontSize={DIVIDER.labelSize}
            fontWeight={DIVIDER.labelWeight}
            letterSpacing={DIVIDER.labelTracking}
            fill={DIVIDER.labelColor}
            fontFamily="var(--font-mono, ui-monospace, SFMono-Regular, monospace)"
            opacity={opacity}
          >
            {b.label}
          </text>
        );
      })}
    </g>
  );
}

export { TIER_BOUNDS };
